import type { ParamMap } from '@angular/router';
import { normalizeToWeekStartMonday } from './my-timesheet-deep-link.util';

export interface RmTimesheetDeepLinkParams {
  employeeId: string;
  startDate: string;
  endDate: string;
  projectType?: string;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True when the URL looks like an RM approval deep link (employee + date range). */
export function hasRmTimesheetDeepLinkAttempt(qp: ParamMap): boolean {
  const emp = qp.get('employeeId')?.trim() || qp.get('userId')?.trim();
  return !!(emp && (qp.get('startDate')?.trim() || qp.get('endDate')?.trim()));
}

/**
 * Parse RM Review & Approval email deep link.
 * @example `?employeeId=310&startDate=2026-06-29&endDate=2026-07-05&projectType=SUPPORT`
 */
export function parseRmTimesheetDeepLink(qp: ParamMap): RmTimesheetDeepLinkParams | null {
  const employeeId = (qp.get('employeeId')?.trim() || qp.get('userId')?.trim() || '');
  if (!employeeId) return null;

  const empNum = Number(employeeId);
  if (!Number.isFinite(empNum) || empNum <= 0 || !Number.isInteger(empNum)) return null;

  const startRaw = qp.get('startDate')?.trim() ?? '';
  const endRaw = qp.get('endDate')?.trim() ?? '';
  if (!startRaw || !endRaw) return null;
  if (!isValidIsoDateString(startRaw) || !isValidIsoDateString(endRaw)) return null;
  if (startRaw > endRaw) return null;

  const weekStart = normalizeToWeekStartMonday(startRaw);
  if (!weekStart) return null;

  const projectType = qp.get('projectType')?.trim();
  return {
    employeeId: String(empNum),
    startDate: weekStart,
    endDate: endRaw,
    ...(projectType ? { projectType } : {}),
  };
}

function isValidIsoDateString(s: string): boolean {
  if (!ISO_DATE_RE.test(s)) return false;
  const d = new Date(`${s}T12:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}` === s;
}
